// Admissions & Waitlist CRM data layer.
//
// `enrollment_records` remains the canonical admission case. Browser clients
// read tenant-scoped workspace data through RLS and make every mutation through
// the narrow RPCs installed by the Admissions migrations.

import { getEnrollmentRecord } from "./enrollment";
import { isSupabaseConfigured, supabase } from "./supabase";

function one(row) {
  return Array.isArray(row) ? row[0] || null : row || null;
}

async function rpc(name, args) {
  if (!isSupabaseConfigured) {
    throw new Error("Admissions actions require a connected HoneyAcorn workspace.");
  }
  const { data, error } = await supabase.rpc(name, args);
  if (error) throw error;
  return data;
}

export async function getAdmissionsDashboard() {
  return (await rpc("rpc_admissions_dashboard", {})) || {};
}

export async function getAdmissionCapacity({ startDate, locationId = null }) {
  return (await rpc("rpc_admission_capacity_openings", {
    p_start_date: startDate || null,
    p_location_id: locationId || null,
  })) || [];
}

export async function getAdmissionWaitlistMatches({ startDate, locationId = null }) {
  return (await rpc("rpc_admission_waitlist_matches", {
    p_start_date: startDate || null,
    p_location_id: locationId || null,
  })) || [];
}

export async function listAdmissionLocations() {
  if (!isSupabaseConfigured) return [];
  const { data, error } = await supabase
    .from("locations")
    .select("id, name")
    .eq("active", true)
    .order("name");
  if (error) throw error;
  return data || [];
}

export async function listAdmissionClassrooms({ locationId = null } = {}) {
  if (!isSupabaseConfigured) return [];
  let query = supabase
    .from("classrooms")
    .select("id, name, age_group, location_id")
    .order("name");
  if (locationId) query = query.eq("location_id", locationId);
  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

export async function promoteInquiryToAdmission({ inquiryId, locationId = null, classroomId = null }) {
  return one(await rpc("rpc_promote_inquiry_to_admission", {
    p_inquiry_id: inquiryId,
    p_location_id: locationId || null,
    p_preferred_classroom_id: classroomId || null,
  }));
}

export async function updateAdmissionDetails({ enrollmentId, expectedUpdatedAt, patch }) {
  return one(await rpc("rpc_update_admission_details", {
    p_enrollment_id: enrollmentId,
    p_expected_updated_at: expectedUpdatedAt,
    p_patch: patch,
  }));
}

export async function getAdmissionWorkspace(enrollmentId) {
  if (!isSupabaseConfigured) {
    return {
      record: await getEnrollmentRecord(enrollmentId),
      tours: [],
      schedulePreferences: [],
      tasks: [],
      communications: [],
      notificationJobs: [],
    };
  }
  const [record, tours, schedulePreferences, tasks, communications, notificationJobs] = await Promise.all([
    getEnrollmentRecord(enrollmentId),
    supabase
      .from("admission_tours")
      .select("id, scheduled_at, status, outcome_note, completed_at, location_id, assigned_to_profile_id, created_at")
      .eq("enrollment_id", enrollmentId)
      .order("scheduled_at", { ascending: false }),
    supabase
      .from("admission_schedule_preferences")
      .select("id, weekday, starts_at, ends_at, effective_on, ends_on")
      .eq("enrollment_id", enrollmentId)
      .order("weekday")
      .order("starts_at"),
    supabase
      .from("admission_tasks")
      .select("id, title, due_on, status, completed_at, assignee_profile_id, created_at")
      .eq("enrollment_id", enrollmentId)
      .order("status")
      .order("due_on", { ascending: true, nullsFirst: false }),
    supabase
      .from("admission_communications")
      .select("id, channel, direction, subject, body, occurred_at, notification_job_id")
      .eq("enrollment_id", enrollmentId)
      .order("occurred_at", { ascending: false })
      .limit(12),
    supabase
      .from("admission_notification_jobs")
      .select("id, kind, status, available_at, sent_at, attempts, last_error, created_at")
      .eq("enrollment_id", enrollmentId)
      .order("created_at", { ascending: false })
      .limit(12),
  ]);

  for (const result of [tours, schedulePreferences, tasks, communications, notificationJobs]) {
    if (result.error) throw result.error;
  }
  return {
    record,
    tours: tours.data || [],
    schedulePreferences: schedulePreferences.data || [],
    tasks: tasks.data || [],
    communications: communications.data || [],
    notificationJobs: notificationJobs.data || [],
  };
}

export async function scheduleAdmissionTour({ enrollmentId, scheduledAt, locationId = null, assigneeProfileId = null }) {
  return one(await rpc("rpc_create_admission_tour", {
    p_enrollment_id: enrollmentId,
    p_scheduled_at: scheduledAt,
    p_location_id: locationId || null,
    p_assigned_to_profile_id: assigneeProfileId || null,
  }));
}

export async function completeAdmissionTour({ tourId, status, outcomeNote = null }) {
  return one(await rpc("rpc_complete_admission_tour", {
    p_tour_id: tourId,
    p_status: status,
    p_outcome_note: outcomeNote || null,
  }));
}

export async function replaceAdmissionSchedulePreferences({ enrollmentId, windows }) {
  return (await rpc("rpc_replace_admission_schedule_preferences", {
    p_enrollment_id: enrollmentId,
    p_windows: windows,
  })) || [];
}

export async function createAdmissionTask({ enrollmentId, title, dueOn = null, assigneeProfileId = null }) {
  return one(await rpc("rpc_create_admission_task", {
    p_enrollment_id: enrollmentId,
    p_title: title,
    p_due_on: dueOn || null,
    p_assignee_profile_id: assigneeProfileId || null,
  }));
}

export async function completeAdmissionTask({ taskId, complete = true }) {
  return one(await rpc("rpc_complete_admission_task", {
    p_task_id: taskId,
    p_complete: complete,
  }));
}

export async function logAdmissionCommunication({
  enrollmentId,
  channel = "internal_note",
  direction = "internal",
  subject = null,
  body = null,
  occurredAt = null,
}) {
  return one(await rpc("rpc_log_admission_communication", {
    p_enrollment_id: enrollmentId,
    p_channel: channel,
    p_direction: direction,
    p_subject: subject || null,
    p_body: body || null,
    p_occurred_at: occurredAt || null,
  }));
}

export async function queueAdmissionNotification({ enrollmentId, kind }) {
  return one(await rpc("rpc_queue_admission_notification", {
    p_enrollment_id: enrollmentId,
    p_kind: kind,
    p_available_at: null,
  }));
}
